import React, { useState, useEffect } from 'react';
import { Container, Button, Form, Modal, Table, Spinner } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import APIHandler from '../utils/APIHandler';

function NetworkManager() {
    const [networks, setNetworks] = useState([]); // Initialize with an empty array
    const [networkName, setNetworkName] = useState('');
    const [ssid, setSsid] = useState('');
    const [passcode, setPasscode] = useState('');
    const [loading, setLoading] = useState(false);
    const [editingNetwork, setEditingNetwork] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const fetchNetworks = async () => {
        setLoading(true);
        try {
            const data = await APIHandler.getNetworks();
            setNetworks(Array.isArray(data.networks) ? data.networks : []); // Ensure data is an array
        } catch (error) {
            toast.error('Error fetching networks');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNetworks();
    }, []);

    const handleShowModal = (network = null) => {
        if (network) {
            setEditingNetwork(network);
            setNetworkName(network.network_name);
            setSsid(network.ssid);
            setPasscode(network.passcode);
        } else {
            setEditingNetwork(null);
            setNetworkName('');
            setSsid('');
            setPasscode('');
        }
        setShowModal(true);
    };

    const handleSaveNetwork = async () => {
        if (!networkName || !ssid || !passcode) {
            toast.error('Please fill in all fields');
            return;
        }

        setLoading(true);
        try {
            if (editingNetwork) {
                await APIHandler.editNetwork(networkName, ssid, passcode);
                toast.success('Network updated successfully');
            } else {
                await APIHandler.addNetwork(networkName, ssid, passcode);
                toast.success('Network added successfully');
            }
            fetchNetworks();
            setShowModal(false);
        } catch (error) {
            toast.error('Error saving network');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteNetwork = async (networkName) => {
        setLoading(true);
        try {
            await APIHandler.deleteNetwork(networkName);
            toast.success('Network deleted successfully');
            fetchNetworks();
        } catch (error) {
            toast.error('Error deleting network');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container className="mt-4">
            <ToastContainer position="top-right" autoClose={5000} />
            <h2>Network Management</h2>
            <Button variant="primary" className="mb-3" onClick={() => handleShowModal()}>
                Add New Network
            </Button>
            {loading ? (
                <div className="text-center">
                    <Spinner animation="border" />
                    <p>Loading...</p>
                </div>
            ) : (
                <Table bordered hover responsive>
                    <thead>
                        <tr>
                            <th>Network Name</th>
                            <th>SSID</th>
                            <th>Passcode</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {networks.map((network) => (
                            <tr key={network.network_name}>
                                <td>{network.network_name}</td>
                                <td>{network.ssid}</td>
                                <td>{network.passcode}</td>
                                <td>
                                    <Button
                                        variant="warning"
                                        className="me-2"
                                        onClick={() => handleShowModal(network)}
                                    >
                                        Edit
                                    </Button>
                                    <Button
                                        variant="danger"
                                        onClick={() => handleDeleteNetwork(network.network_name)}
                                    >
                                        Delete
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{editingNetwork ? 'Edit Network' : 'Add New Network'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Network Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter network name"
                                value={networkName}
                                onChange={(e) => setNetworkName(e.target.value)}
                                disabled={!!editingNetwork} // Prevent changing name when editing
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>SSID</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter SSID"
                                value={ssid}
                                onChange={(e) => setSsid(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Passcode</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter Passcode"
                                value={passcode}
                                onChange={(e) => setPasscode(e.target.value)}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSaveNetwork}>
                        Save
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
}

export default NetworkManager;
